import type { GaiaJsonValue } from '@/api';
import { isEffectivelyEmpty } from './displayAiResponse';

// Define the keys that can be overwritten
export type OverwriteKeys =
  'value' | 'reason' | 'page' | 'highlighted_text' | 'evaluation' | 'evaluation_reason';

// All possible overwrite keys
export const ALL_OVERWRITE_KEYS: OverwriteKeys[] = [
  'value',
  'reason',
  'page',
  'highlighted_text',
  'evaluation',
  'evaluation_reason',
];

// Evaluation mapping for converting display values to API values
export const EVALUATION_MAPPING: Record<string, string> = {
  high: 'High',
  medium: 'Medium',
  review: 'Human Review',
  low: 'Low',
};

export interface EditValues {
  value: GaiaJsonValue;
  reason: string;
  page: string;
  highlighted_text: string[];
  evaluation: string;
  evaluation_reason: string;
}

export interface ExistingOverwrite {
  value?: GaiaJsonValue;
  reason?: string | null;
  page?: number | null;
  highlighted_text?: string[] | null;
  evaluation?: GaiaJsonValue;
  evaluation_reason?: string | null;
}

/**
 * Checks if an overwrite value is different from the original AI value
 */
export function isValueOverwritten(
  overwriteValue: GaiaJsonValue,
  originalValue: GaiaJsonValue
): boolean {
  // If there's no overwrite value at all, it's not overwritten
  if (overwriteValue === undefined) {
    return false;
  }

  // If overwrite value is null, we need to check if this represents an actual overwrite
  // or just the absence of an overwrite.
  if (overwriteValue === null) {
    // Only consider it overwritten if the original value is not null/undefined/empty
    // AND if this null value represents an explicit user action (not just absence of overwrite)
    // For now, we'll be conservative and only consider it overwritten if the original is not empty
    // and we have a clear indication this is an explicit overwrite
    return !isEffectivelyEmpty(originalValue);
  }

  // If there's no original value, but there's an overwrite value, check if overwrite is effectively empty
  if (originalValue === null || originalValue === undefined) {
    // Only consider it overwritten if the overwrite value is not effectively empty
    return !isEffectivelyEmpty(overwriteValue);
  }

  // If both values are effectively empty, it's not overwritten
  if (isEffectivelyEmpty(originalValue) && isEffectivelyEmpty(overwriteValue)) {
    return false;
  }

  // Compare the values using JSON.stringify for deep comparison
  return JSON.stringify(overwriteValue) !== JSON.stringify(originalValue);
}

/**
 * Builds a payload for overwrite operations by preserving existing overwrites
 * and merging in new edits.
 */
export function buildOverwritePayload(
  existingOverwrite: ExistingOverwrite | null,
  edits: Map<string, EditValues>
): Record<string, GaiaJsonValue> {
  const payload: Record<string, GaiaJsonValue> = {};

  if (existingOverwrite) {
    for (const key of ALL_OVERWRITE_KEYS) {
      const existingValue = existingOverwrite[key];
      if (existingValue !== null && existingValue !== undefined) {
        payload[key] = existingValue;
      }
    }
  }

  for (const [key, values] of edits) {
    switch (key) {
      case 'ai_value':
        payload.value = values.value;
        break;
      case 'ai_reason':
        payload.reason = values.reason;
        break;
      case 'ai_page':
        payload.page = values.page ? Number(values.page) : null;
        break;
      case 'ai_highlighted_text':
        payload.highlighted_text =
          values.highlighted_text.length > 0 ? values.highlighted_text : null;
        break;
      case 'evaluation':
        payload.evaluation = EVALUATION_MAPPING[values.evaluation] || values.evaluation;
        break;
      case 'evaluation_reason':
        payload.evaluation_reason = values.evaluation_reason;
        break;
    }
  }

  return payload;
}
